import * as THREE from 'three'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import { moveObject3DChildToIndex } from '../../utils/scene-object/addObject3DToParent'

/**
 * Puppy 装配组件变换根物化器。
 *
 * `.puppy` 的 display.glb 可以让 assembly/part occurrence 直接落在 Mesh、Line 或 Points 上，
 * 但这些对象同时承担显示和位姿会让拓扑代理、选择代理与移动命令争用同一个叶子对象。本类在
 * assembly hierarchy 建立运行时绑定前，为这类 occurrence 创建独立 Group，并保持显示资源不变。
 */
export class PuppyAssemblyComponentRootMaterializer {
  /**
   * 物化缺失的组件根并返回新的 assemblyNodeId 对象索引。
   *
   * @param input 根装配身份与导入阶段收集的运行时对象索引。
   * @returns 每个被包装 occurrence 均以新 Group 作为 node host 的对象索引。
   */
  public materialize(input: {
    readonly rootAssemblyNodeId: string
    readonly objectsByAssemblyNodeId: ReadonlyMap<string, readonly THREE.Object3D[]>
    readonly componentNameByAssemblyNodeId: Readonly<Record<string, string>>
  }): Map<string, THREE.Object3D[]> {
    const materializedObjectsByAssemblyNodeId = new Map<string, THREE.Object3D[]>()

    input.objectsByAssemblyNodeId.forEach((objects, assemblyNodeId) => {
      materializedObjectsByAssemblyNodeId.set(assemblyNodeId, [...objects])
    })

    materializedObjectsByAssemblyNodeId.forEach((objects, assemblyNodeId) => {
      if (assemblyNodeId === input.rootAssemblyNodeId) {
        return
      }

      const sourceHost = objects.find((object) => this.isAssemblyNodeHost(object))
      if (!sourceHost || !this.isRenderableObject(sourceHost)) {
        return
      }

      const componentName = input.componentNameByAssemblyNodeId[assemblyNodeId]?.trim() ?? ''
      if (!componentName) {
        throw new Error(`Puppy 组件根缺少 occurrence 名称：assemblyNodeId=${assemblyNodeId}`)
      }
      const componentRoot = this.wrapRenderableHost(
        sourceHost,
        assemblyNodeId,
        componentName,
      )
      materializedObjectsByAssemblyNodeId.set(
        assemblyNodeId,
        [componentRoot, ...objects],
      )
    })

    return materializedObjectsByAssemblyNodeId
  }

  /**
   * 判断对象是否是 display.glb 明确声明的 assembly node host。
   *
   * @param object 待检查的运行时对象。
   * @returns 对象是否承载 `assemblyNodeRole=node`。
   */
  private isAssemblyNodeHost(object: THREE.Object3D): boolean {
    return ImportedModelObjectMetadataService
      .readPuppyOwnerMetadata(object)?.assemblyNodeRole === 'node'
  }

  /**
   * 判断对象是否属于不能直接承载组件位姿的可渲染叶子。
   *
   * @param object 待检查的运行时对象。
   * @returns Mesh、Line 或 Points 返回 `true`。
   */
  private isRenderableObject(object: THREE.Object3D): boolean {
    return object instanceof THREE.Mesh
      || object instanceof THREE.Line
      || object instanceof THREE.Points
  }

  /**
   * 在原父节点和原兄弟槽位创建组件 Group。
   *
   * Group 承接显示对象的完整局部 TRS；显示对象归一化为单位局部矩阵，因此显示几何、拓扑
   * 后代和共享 Geometry/Material 的世界结果保持不变。该过程不克隆任何渲染资源。
   *
   * @param sourceHost 直接承载 assembly node 身份的可渲染对象。
   * @param assemblyNodeId 当前 occurrence 的稳定装配身份。
   * @param componentName 当前 occurrence 的业务名称。
   * @returns 已挂入原场景位置的独立组件 Group。
   */
  private wrapRenderableHost(
    sourceHost: THREE.Object3D,
    assemblyNodeId: string,
    componentName: string,
  ): THREE.Group {
    const parent = sourceHost.parent
    if (!parent) {
      throw new Error(`Puppy 组件显示对象缺少父节点：assemblyNodeId=${assemblyNodeId}, objectId=${sourceHost.uuid}`)
    }

    if (sourceHost.matrixAutoUpdate) {
      sourceHost.updateMatrix()
    }
    const sourceLocalMatrix = sourceHost.matrix.clone()
    const siblingIndex = parent.children.indexOf(sourceHost)
    const sourcePuppy = ImportedModelObjectMetadataService.readPuppyOwnerMetadata(sourceHost)
    const componentRootPuppy: Record<string, unknown> = {
      ...(sourcePuppy ?? {}),
      assemblyNodeId,
      assemblyNodeRole: 'node',
      displayNodeId: undefined,
    }

    const componentRoot = new THREE.Group()
    // 组件根代表 occurrence，显示 Mesh 名称只属于 GLB 技术节点，不能上提为业务名称。
    componentRoot.name = componentName
    componentRoot.matrix.copy(sourceLocalMatrix)
    componentRoot.matrix.decompose(
      componentRoot.position,
      componentRoot.quaternion,
      componentRoot.scale,
    )
    ImportedModelObjectMetadataService.writePuppyOwnerMetadata(
      componentRoot,
      componentRootPuppy,
    )

    parent.remove(sourceHost)
    parent.add(componentRoot)
    if (siblingIndex >= 0) moveObject3DChildToIndex(parent, componentRoot, siblingIndex)
    componentRoot.add(sourceHost)

    ImportedModelObjectMetadataService.writePuppyOwnerMetadata(sourceHost, {
      ...(sourcePuppy ?? {}),
      assemblyNodeId,
      assemblyNodeRole: 'display',
    })
    sourceHost.position.set(0, 0, 0)
    sourceHost.quaternion.identity()
    sourceHost.scale.set(1, 1, 1)
    sourceHost.matrix.identity()
    if (sourceHost.matrixAutoUpdate) {
      sourceHost.updateMatrix()
    }

    return componentRoot
  }
}
